<script>
import { Bar } from "vue-chartjs";
import Chart from "chart.js";
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";
Chart.plugins.register(ChartAnnotationsPlugin);

export default {
  extends: Bar,
  name: "SuijinChart",
  props: {
    selectedObservatory: {
      type: Object
    },
    chartTimeSeries: {
      type: Array
    },
    minDate: Object,
    maxDate: Object,
    chartWaterBoxLevel: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      landform: [],
      dataCollection: {
        labels: [],
        datasets: []
      },
      options: {
        layout: {
          padding: {
            right: 0,
            left: 0
          }
        },
        tooltips: {
          enabled: true,
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const unit = data.datasets[tooltipItem.datasetIndex].unit;
              return (
                label +
                " : " +
                data.datasets[tooltipItem.datasetIndex].data[
                  tooltipItem.index
                ] +
                " " +
                unit
              );
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: "level",
              gridLines: {
                display: false
              },
              ticks: {
                max: 20,
                min: 0,
                stepSize: 1,
                callback: tick => {
                  return ("        " + tick).slice(-6);
                }
              }
            },
            {
              id: "flow",
              position: "right",
              gridLines: {
                display: false
              },
              display: false,
              ticks: {
                max: 20,
                min: 0
              }
            }
          ],
          xAxes: [
            {
              id: "data",
              type: "time",
              gridLines: {
                display: false
              },
              time: {
                unit: "hour",
                unitStepSize: 1,
                tooltipFormat: "MM/DD HH:mm",
                displayFormats: {
                  minute: "HH:mm",
                  hour: "HH:mm",
                  day: "HH:mm",
                  week: "HH:mm",
                  month: "HH:mm"
                }
              },
              ticks: {
                display: true,
                autoSkip: true
              }
            }
          ]
        },
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "index",
          intersect: false
        },
        groundDataset: [],
        annotation: {
          annotations: [
            {
              type: "box",
              drawTime: "beforeDatasetsDraw",
              yScaleID: "level",
              borderColor: "rgba(0,0,0,0)",
              yMin: 0,
              yMax: 0,
              backgroundColor: "#3D8AFF"
            },
            {
              type: "line",
              mode: "vertical",
              scaleID: "data",
              value: "02:00",
              borderColor: "#FF0000",
              borderWidth: 1.5,
              label: {
                content: "現在",
                position: "top",
                enabled: true
              }
            }
          ]
        }
      }
    };
  },
  computed: {
    baseDate() {
      return this.$store.getters.baseDate.clone();
    },
    sensors() {
      if (this.selectedObservatory.sensors) {
        let sensors = [...this.selectedObservatory.sensors];
        sensors.sort((a, b) => b.level - a.level);
        return sensors;
      } else {
        return [];
      }
    }
  },
  watch: {
    chartTimeSeries() {
      this.reRender();
    },
    chartWaterBoxLevel() {
      this.reRender();
    }
  },
  mounted() {
    const carTateImage = this.$store.getters.suijinChartImage.carTate;
    const carYokoImage = this.$store.getters.suijinChartImage.carYoko;
    const poleImage = this.$store.getters.suijinChartImage.pole;
    this.addPlugin({
      id: "horizontalLine",
      beforeDatasetsDraw: chart => {
        const groundDataset = chart.config.options.groundDataset;
        if (groundDataset.length === 0) {
          return;
        }
        const ctx = chart.chart.ctx;
        const xAxe = chart.scales["data"];
        const yAxe = chart.scales["level"];
        const arrX = groundDataset.map(o => {
          return o[0];
        });
        const arrY = groundDataset.map(o => {
          return o[1];
        });
        const minGround = groundDataset.find(o => o[0] === Math.min(...arrX));
        const maxGround = groundDataset.find(o => o[0] === Math.max(...arrX));
        const maxGroundY = Math.max(...arrY);

        const getX = baseX => {
          return (
            ((baseX - minGround[0]) / (maxGround[0] - minGround[0])) *
              xAxe.width +
            xAxe.left
          );
        };

        const getY = baseY => {
          return (
            ((yAxe.max - yAxe.min - (baseY - yAxe.min)) /
              (yAxe.max - yAxe.min)) *
              yAxe.height +
            yAxe.top
          );
        };

        // landform
        ctx.beginPath();
        ctx.moveTo(xAxe.left, yAxe.bottom);
        for (let i = 0; i < groundDataset.length; i++) {
          ctx.lineTo(getX(groundDataset[i][0]), getY(groundDataset[i][1]));
        }
        ctx.lineTo(xAxe.right, yAxe.bottom);
        ctx.lineTo(xAxe.left, yAxe.bottom);
        ctx.closePath();
        ctx.fillStyle = "#cf9e80";
        ctx.fill();

        // 道路面
        const road_offset = 0.3;
        const road_y = getY(maxGroundY - road_offset / 2);
        for (let i = 1; i < groundDataset.length; i++) {
          if (
            groundDataset[i - 1][1] !== maxGroundY ||
            groundDataset[i][1] !== maxGroundY
          ) {
            continue;
          }
          ctx.strokeStyle = "#9e9e9e";
          ctx.lineWidth = getY(0) - getY(road_offset);
          ctx.beginPath();
          ctx.setLineDash([]);
          ctx.moveTo(getX(groundDataset[i - 1][0]), road_y);
          ctx.lineTo(getX(groundDataset[i][0]), road_y);
          ctx.stroke();
        }

        // 線形グラデーションを作成
        const sensorMaxLevel = Math.max(...this.sensors.map(s => s.level));
        if (
          this.chartWaterBoxLevel !== null &&
          sensorMaxLevel === this.chartWaterBoxLevel
        ) {
          const gradientHeight = 0.6;
          const gradient = ctx.createLinearGradient(
            0,
            getY(gradientHeight),
            0,
            getY(sensorMaxLevel)
          );
          gradient.addColorStop(0, "#3D8AFF00");
          gradient.addColorStop(1, "#3D8AFFFF");
          ctx.fillStyle = gradient;
          ctx.fillRect(
            xAxe.left,
            getY(gradientHeight),
            xAxe.right - xAxe.left,
            getY(sensorMaxLevel) - getY(gradientHeight)
          );
        }

        // sensor lines
        ctx.save();
        this.sensors.forEach((sensor, i) => {
          ctx.setLineDash([5, 5]);
          ctx.strokeStyle = i === 0 ? "#ad02ad" : "#ffd600";
          ctx.lineWidth = 3;
          const yPosition = yAxe.getPixelForValue(sensor.level);
          ctx.beginPath();
          ctx.moveTo(xAxe.left, yPosition);
          ctx.lineTo(xAxe.right, yPosition);
          ctx.stroke();
        });
        ctx.restore();

        // 画像
        const carImage = ["12217_11", "12217_12"].includes(
          this.selectedObservatory.id
        )
          ? carTateImage
          : carYokoImage;
        const roadGroundX = groundDataset
          .filter(r => r[1] === maxGroundY)
          .map(r => r[0]);
        if (carImage !== null) {
          const h = getY(0) - getY(1.5);
          let w = h * (carImage.width / carImage.height);
          ctx.drawImage(
            carImage,
            getX(Math.min(...roadGroundX) + 14),
            getY(maxGroundY) - h,
            w,
            h
          );
        }
        if (poleImage !== null) {
          const h = getY(0) - getY(5);
          let w = h * (poleImage.width / poleImage.height);
          ctx.drawImage(
            poleImage,
            getX(Math.min(...roadGroundX) + 1),
            getY(maxGroundY) - h,
            w,
            h
          );
        }
      }
    });
    setTimeout(() => {
      this.reRender();
    }, 500);
  },
  methods: {
    reRender() {
      this.setTimeSeries();
      this.renderChart(this.dataCollection, this.options);
    },

    setTimeSeries() {
      this.options.annotation.annotations.splice(
        2,
        this.options.annotation.annotations.length - 2
      );

      // date range
      this.options.scales.xAxes[0].ticks.min = this.minDate.toDate();
      this.options.scales.xAxes[0].ticks.max = this.maxDate.toDate();

      // labels
      this.dataCollection.labels = this.chartTimeSeries.map(ts => {
        return ts.jst.toDate();
      });

      // datasets
      this.dataCollection.datasets = this.sensors.map((sensor, i) => {
        const data = this.chartTimeSeries.map(ts => ts.sensorLevels[sensor.id]);
        return {
          type: "line",
          label: `センサ${i + 1}`,
          unit: "m",
          pointBackgroundColor: "white",
          fill: false,
          yAxisID: "level",
          borderWidth: 2,
          borderColor: "#01163F",
          backgroundColor: "#01163F",
          pointBorderColor: "#01163F",
          pointRadius: 2,
          pointHitRadius: 0,
          lineTension: 0,
          data: data,
          spanGaps: true
        };
      });

      // landform
      this.landform = [];
      if (
        this.selectedObservatory.landform &&
        this.selectedObservatory.landform.length > 0
      ) {
        this.landform = this.selectedObservatory.landform;
      }
      this.options.groundDataset = this.landform;

      this.setYAxesLimitData();
      this.setNowStrokeData();
      this.setWaterBoxData();
    },

    setYAxesLimitData() {
      let levels = this.dataCollection.datasets[0].data;
      levels = levels.filter(l => l !== null).map(l => parseFloat(l));

      let min = Math.min(...levels) - 1;
      let max = Math.max(...levels) + 2;

      if (this.landform.length > 0) {
        const yArr = this.landform.map(lf => lf[1]);
        const landformMaxLevel = Math.max(...yArr) + 2;
        if (isNaN(max) || landformMaxLevel > max) {
          max = landformMaxLevel;
        }
        if (max < 5) {
          max = 5;
        }
        min = Math.min(...yArr) - 0.5;
      }

      if (isNaN(min)) {
        if (this.chartWaterBoxLevel === null) {
          min = 0;
        } else {
          min = this.chartWaterBoxLevel - 1;
        }
      }

      this.options.scales.yAxes[0].ticks.min = Math.floor(min);
      this.options.scales.yAxes[0].ticks.max = Math.floor(max);
      this.options.annotation.annotations[0].yMin = Math.floor(min);
    },

    setNowStrokeData() {
      this.options.annotation.annotations[1].value = this.baseDate
        .local()
        .toDate();
    },

    setWaterBoxData() {
      this.options.annotation.annotations[0].yMin = this.options.scales.yAxes[0].ticks.min;
      if (this.chartWaterBoxLevel !== null) {
        this.options.annotation.annotations[0].yMax = this.chartWaterBoxLevel;
      } else {
        this.options.annotation.annotations[0].yMax = this.options.scales.yAxes[0].ticks.min;
      }
    }
  }
};
</script>

<style scoped></style>
